import { GridFilterModel, GridLinkOperator, GridRowId, GridRowsProp, GridSelectionModel } from "@mui/x-data-grid-pro";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { AppContext } from "../../../core/context/appContextProvider";
import useRole from "../../../core/routing/useRole";
import { disconnectGL, getAccountsForFund, getGLAuthQuickbooks, getGlConnectionDetails, getHasMappingAdmin, getMappedFunds, getTransactionBuckets, getTransactionTypes, postMapsForFund, syncAccountsForFund } from '../../../services/fund.service';
import { GENERIC_ERROR_MESSAGE } from "../../../utils/constants/text.constants";
import { DateTimeFormat } from "../../../utils/helpers/format.helper";
import { useEffectAsync } from '../../../utils/hooks/useEffectAsync.hook';
import { FundMapped, GlMapping, TransMapItem, TransType, TransTypeBucket } from '../../../utils/types/fund.type';
import { ScopeRole } from "../../../utils/types/user.type";
import { DISCONNECT_GL_ERROR, DISCONNECT_GL_SUCCESS, SAVE_MAPPING_ERROR, SAVE_MAPPING_SUCCESS, SYNC_GL_ERROR, SYNC_GL_SUCCESS } from "./TransactionMapper.constants";

export enum glConnection {
  QUICKBOOKS_ONLINE = "QuickBooks Online" as any
}

export const useTransactionMapperEffect = () => {

  const { fundId } = useParams<{ fundId: string }>();
  const { state, informationAlert } = useContext(AppContext);
  const clientId = state.loginUser.clientId;

  const filterModelUnmappedOnly = {
    items: [{ columnField: 'transactionTypeName', operatorValue: 'isEmpty', value: '' }],
  };
  const filterModelAll = { items: [] };

  const [glMapping, setGlMapping] = useState<GlMapping>();
  const [glMappingResponse, setGlMappingResponse] = useState<GlMapping | null>(null);
  const [glConnectionDate, setGlConnectionDate] = useState<string | null>(null);
  const [glConnectionPlatform, setGlConnectionPlatform] = useState<string | null>(null);
  const [glConnectionUsername, setGlConnectionUsername] = useState<string | null>(null);
  const [glPlatform, setGlPlatform] = useState<string>('');
  const [dataGridRows, setDataGridRows] = useState<GridRowsProp>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(filterModelUnmappedOnly);
  const [fundsMapped, setFundsMapped] = useState<FundMapped[]>([]);
  const [fundName, setFundName] = useState<string>('');
  const [hasMappingAdmin, setHasMappingAdmin] = useState<boolean>(false);
  const [progress, setProgress] = useState<{ mappedAmount: number, totalAmount: number }>({ mappedAmount: 0, totalAmount: 0 });
  const [searchValue, setSearchValue] = useState('');
  const [transTypes, setTransTypes] = useState<TransType[]>([]);
  const [transBuckets, setTransBuckets] = useState<TransTypeBucket[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [tab, setTab] = useState<number>(0);
  const [bulkMappingSelection, setBulkMappingSelection] = useState<string>('');
  const [isAlertToUnlockOpen, setIsAlertToUnlockOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isScreenLocked, setIsScreenLocked] = useState<boolean>(true);
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [transOptions, setTransOptions] = useState<string[]>([]);
  const [isGLConnected, setIsGLConnected] = useState<boolean>(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
  const [isSidePanelLoading, setIsSidePanelLoading] = useState<boolean>(false);
  const [isReadyToDisconnect, setIsReadyToDisconnect] = useState<boolean>(false);
  const [glSetupCurrentIndex, setGlSetupCurrentIndex] = useState<number>(1);

  const { hasRole: isFundAdmin } = useRole([ScopeRole.FUND_USER_ADMIN]);

  const readonly: boolean = !!isFundAdmin;

  const glSetupSteps = [
    { label: 'Select General Ledger', description: 'Select a General Ledger below' },
    { label: 'Authenticate', description: '' }
  ];

  useEffectAsync(async (isCanceled) => {
    try {
      setIsLoading(true);

      let glMappingResponse;

      const isAuthenticated = await checkGLConnection(isCanceled);

      if (isAuthenticated) {
        if (isCanceled()) return;
        glMappingResponse = await getAccountsForFund(fundId);

        if (glMappingResponse && glMappingResponse.accounts.length === 0) {
          glMappingResponse = await syncAccountsForFund(fundId);
        }
      }

      if (isCanceled()) return;
      const hasMappingAdminResponse = await getHasMappingAdmin();

      if (isCanceled()) return;
      const transactionTypesResponse = await getTransactionTypes(clientId);

      transactionTypesResponse.unshift({ id: '1', name: 'Do not map' });

      if (isCanceled()) return;
      const fundsMappedResponse = await getMappedFunds();

      if (isCanceled()) return;
      const transactionBucketsResponse = await getTransactionBuckets(clientId);

      if (isCanceled()) return;
      setIsLoading(false);

      setHasMappingAdmin(hasMappingAdminResponse);

      if (glMappingResponse) {
        setGlMapping(glMappingResponse);
        setFundName(glMappingResponse.fundName);
      }

      setTransTypes(transactionTypesResponse);
      setFundsMapped(fundsMappedResponse);
      setTransBuckets(transactionBucketsResponse);
    } catch (e) {
      informationAlert(GENERIC_ERROR_MESSAGE, "error");
    }
  }, []);

  const getGLConnection = async (isCanceled?: () => boolean) => {
    try {
      setIsLoading(true);

      if (isCanceled?.()) return;
      const data = await getGlConnectionDetails(fundId);

      setIsLoading(false);

      return data.connectionDetails;
    } catch (e) {
      informationAlert(GENERIC_ERROR_MESSAGE, "error");
    }
  };

  const checkGLConnection = async (isCanceled?: () => boolean) => {
    const glConnectionDetails = await getGLConnection(isCanceled);

    if (glConnectionDetails && glConnectionDetails.status === 'AUTHENTICATED') {
      setIsGLConnected(true);
      setGlConnectionUsername(glConnectionDetails.linkedBy);
      setGlConnectionDate(DateTimeFormat.shortDate(glConnectionDetails.linkedOn));

      const platform = glConnectionDetails.glPlatform;

      setGlConnectionPlatform(glConnection[platform]);

      return true;
    } else {
      setIsGLConnected(false);
    }
  };

  const getGLAuthUrl = async () => {
    try {
      const data = await getGLAuthQuickbooks(fundId, window.location.href);

      return data.authUrl;
    } catch (e) {
      informationAlert(GENERIC_ERROR_MESSAGE, "error");
    }
  };

  useEffect(() => {
    const transOptions = transTypes.map(type => type.name);

    setTransOptions(transOptions);
  }, [transTypes]);

  const getUpdatedTransTypes = async () => {
    try {
      const transactionTypesResponse = await getTransactionTypes(clientId);

      setTransTypes(transactionTypesResponse);
    } catch (e) {
      informationAlert(GENERIC_ERROR_MESSAGE, "error");
    }
  };

  useEffect(() => {
    if (glMapping) {
      calculateProgress();
      prepMapItemsForDataGrid();
    }
  }, [glMapping]);

  const prepMapItemsForDataGrid = () => {
    if (glMapping) {
      const rows = glMapping.accounts.reduce((newArray: any[], account) => {
        const newItem = { ...account, path: [account.name] };

        newArray.push(newItem);

        if (account.subAccounts) {
          account.subAccounts.map(subAccount => {
            const newChildItem = { ...subAccount, path: [account.name, subAccount.name] };

            newArray.push(newChildItem);
          });
        }

        return newArray;
      }, []);

      setDataGridRows(rows);
    }
  };

  const flattenMapItems = (accounts: TransMapItem[]) => {
    return accounts.reduce((newArray: any[], account) => {
      newArray.push(account);

      if (account.subAccounts) {
        account.subAccounts.map(subAccount => {
          newArray.push(subAccount);
        });
      }

      return newArray;
    }, []);
  };

  const calculateProgress = () => {
    if (glMapping) {
      const flattenedAccounts = flattenMapItems(glMapping.accounts);

      const mappedAmount = flattenedAccounts.filter(item => item.transactionTypeId).length;
      const totalAmount = flattenedAccounts.length;

      setProgress({ mappedAmount, totalAmount });
      if (mappedAmount === totalAmount) {
        setTab(1);
        setFilterModel(filterModelAll);
      }
    }
  };

  const getFilterModel = (value: string) => {
    return {
      items: [
        {
          id: 1,
          columnField: "__tree_data_group__",
          operatorValue: "contains",
          value: value
        },
        { id: 2, columnField: "transactionTypeName", operatorValue: "contains", value: value }
      ],
      linkOperator: GridLinkOperator.Or,
    };
  };

  const mapFunds = (importedFundMapping: GlMapping) => {
    if (importedFundMapping && glMapping && dataGridRows) {
      const newGlMapping = { ...glMapping };

      const importedAccounts = flattenMapItems(importedFundMapping?.accounts)
        .filter(account => account.transactionTypeName !== null && account.transactionTypeId !== null);

      importedAccounts.map(imported => {
        const transactionTypeId = imported.transactionTypeId;
        const transactionTypeName = imported.transactionTypeName;
        const matchingGlAccount = dataGridRows.find(row => row.name === imported.name);

        if (matchingGlAccount && matchingGlAccount.id) {
          newGlMapping.accounts = updateMapItems(newGlMapping.accounts, matchingGlAccount.id, transactionTypeId, transactionTypeName);
        }
      });

      setGlMapping(newGlMapping);
      informationAlert("Imported fund mapped!", "success");
      setIsDirty(true);
    }
  };

  const updateMapItems = (accounts: TransMapItem[], id: string, transactionTypeId: string | null, transactionTypeName: string | null) => {
    const newDateMapped = new Date().toDateString();

    const newMapItems = accounts.reduce((newArray: any[], account) => {
      const newAccount: TransMapItem = { ...account };

      if (account.id === id) {
        newAccount.transactionTypeName = transactionTypeName;
        newAccount.transactionTypeId = transactionTypeId;
        newAccount.dateMapped = newDateMapped;

      } else {
        if (account.subAccounts) {
          const newChildMappings = account.subAccounts.reduce((newChildArray: any[], subAccount) => {
            const newSubAccount: TransMapItem = { ...subAccount };

            if (subAccount.id === id) {
              newSubAccount.transactionTypeName = transactionTypeName;
              newSubAccount.transactionTypeId = transactionTypeId;
              newSubAccount.dateMapped = newDateMapped;
            }
            newChildArray.push(newSubAccount);

            return newChildArray;
          }, []);

          newAccount.subAccounts = [...newChildMappings];
        }
      }
      newArray.push(newAccount);

      return newArray;
    }, []);

    return newMapItems;
  };

  const handleImportFundForMapping = async () => {
    if (selectedFund) {
      try {
        setIsLoading(true);
        const importedFundMapping = await getAccountsForFund(selectedFund);

        setIsLoading(false);
        mapFunds(importedFundMapping);

      } catch (e) {
        informationAlert(GENERIC_ERROR_MESSAGE, "error");
      }
    }
  };

  const checkForSubaccounts = (id: string) => {
    let accounts: string[] = [];

    let updatedRow = dataGridRows.find(row => row.id === id);

    if (updatedRow && updatedRow.parentId) {
      const parentId = updatedRow.parentId;

      updatedRow = dataGridRows.find(row => row.id === parentId);
    }

    if (updatedRow && updatedRow.subAccounts) {
      const subIds = updatedRow.subAccounts.map((sub: TransMapItem) => sub.id);

      accounts = [updatedRow.id, ...subIds];

      return accounts;
    }
    return;
  };

  const handleTransTypeChange = (accounts: string[] | GridRowId[], selectedId: string) => {
    if (glMapping && transTypes) {
      const newGlMapping = { ...glMapping };
      let totalAccounts: string[] = [];
      let transactionTypeId: string | null;
      let transactionTypeName: string | null;

      switch (selectedId) {
        case '+':
          setIsModalOpen(true);
          break;
        default: {
          if (selectedId === '0') {
            transactionTypeId = null;
            transactionTypeName = null;
          } else {
            transactionTypeId = selectedId;
            const arkTransType = transTypes.find(type => type.id === selectedId);

            if (arkTransType) {
              transactionTypeName = arkTransType?.name;
            }
          }

          const stringAccounts = accounts.map(String);

          totalAccounts = stringAccounts.reduce((newArray: string[], id: string) => {
            const subAccounts = checkForSubaccounts(id);

            if (subAccounts) {
              return [...newArray, ...subAccounts];
            }
            newArray.push(id);
            return newArray;
          }, []);

          totalAccounts = Array.from(new Set(totalAccounts));

          totalAccounts.map((id: string) => {
            const updatedRow = dataGridRows.find(row => row.id === id);

            newGlMapping.accounts = updateMapItems(newGlMapping.accounts, updatedRow?.id, transactionTypeId, transactionTypeName);
          });

          setGlMapping(newGlMapping);
          setIsDirty(true);
          setBulkMappingSelection('');
          const text = totalAccounts.length > 1 ? "accounts have" : "account has";

          informationAlert(`${totalAccounts.length} ${text} been updated.`, "success");

          break;
        }
      }
    }
  };

  const handleDownloadChangeLog = () => {
    // TODO
    alert('TODO: Download change log');
  };

  const handleSaveMapping = async () => {
    if (glMapping) {
      const mappings = glMapping.accounts.reduce((newArray: any[], account) => {
        const updatedAccount = { id: account.id, transactionTypeId: account.transactionTypeId };

        newArray.push(updatedAccount);

        if (account.subAccounts) {
          account.subAccounts.map(subAccount => {
            const updatedSubAccount = { id: subAccount.id, transactionTypeId: subAccount.transactionTypeId };

            newArray.push(updatedSubAccount);
          });
        }

        return newArray;
      }, []);

      const data = {
        fundId,
        mappings
      };

      try {
        setIsSending(true);
        await postMapsForFund(data);

        informationAlert(SAVE_MAPPING_SUCCESS, "success");
        setIsSending(false);
        setIsDirty(false);
      } catch (e) {
        informationAlert(SAVE_MAPPING_ERROR, "error");
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);

    newValue === 0 ? setFilterModel(filterModelUnmappedOnly) : setFilterModel(filterModelAll);
  };

  const handleNewTransactionSave = () => {
    getUpdatedTransTypes();
    informationAlert('New transaction type saved.', "success");
  };

  const handleSearchTextChange = (value: string) => {
    setSearchValue(value);
    setFilterModel(getFilterModel(value));
  };

  const handleCancelGL = () => {
    onSidePanelClose();
  };

  const authQuickBooks = async () => {
    const authUrl = await getGLAuthUrl();

    window.location.href = authUrl;
  };

  const handleConnectGL = async (glPlatform: string) => {
    setGlSetupCurrentIndex(2);
    setIsSidePanelLoading(true);
    switch (glPlatform) {
      case 'qb':
      default:
        try {
          await authQuickBooks();
        } catch (e) {
          informationAlert(GENERIC_ERROR_MESSAGE, "error");
        }
    }
  };

  const handleSelectGL = (e: any) => {
    e.stopPropagation();
    setGlPlatform(e.target.id);
  };

  const handleSyncGL = async (e: any) => {
    try {
      const glMappingResponse = await syncAccountsForFund(fundId);

      setGlMapping(glMappingResponse);
      informationAlert(SYNC_GL_SUCCESS, "success");
    } catch (e) {
      informationAlert(SYNC_GL_ERROR, "error");
    }
  };

  const onSidePanelClose = () => {
    setIsSidePanelOpen(false);
    setGlPlatform('');
    setGlSetupCurrentIndex(1);
  };

  const handleDisconnect = () => {
    setIsReadyToDisconnect(true);
  };

  const handleCancelDisconnect = () => {
    setIsReadyToDisconnect(false);
  };

  const handleConfirmDisconnect = async () => {
    setIsReadyToDisconnect(false);
    try {
      setIsLoading(true);

      await disconnectGL({ fundId });
      informationAlert(DISCONNECT_GL_SUCCESS, "success");

      setIsLoading(false);

      await checkGLConnection();
    } catch (error) {
      informationAlert(DISCONNECT_GL_ERROR, "error");
      setIsLoading(false);
    }
  };

  return {
    bulkMappingSelection,
    clientId,
    dataGridRows,
    filterModel,
    fundId,
    fundsMapped,
    fundName,
    glMapping,
    glConnectionDate,
    glConnectionPlatform,
    glConnectionUsername,
    glPlatform,
    glSetupCurrentIndex,
    glSetupSteps,
    readonly,
    handleCancelGL,
    handleConnectGL,
    handleSelectGL,
    handleDownloadChangeLog,
    handleImportFundForMapping,
    handleNewTransactionSave,
    handleSaveMapping,
    handleSearchTextChange,
    handleSyncGL,
    handleTabChange,
    handleTransTypeChange,
    handleDisconnect,
    handleCancelDisconnect,
    handleConfirmDisconnect,
    hasMappingAdmin,
    isAlertToUnlockOpen,
    isDirty,
    isGLConnected,
    isLoading,
    isModalOpen,
    isScreenLocked,
    isSending,
    isSidePanelLoading,
    isSidePanelOpen,
    onSidePanelClose,
    isReadyToDisconnect,
    progress,
    searchValue,
    selectedFund,
    selectionModel,
    setBulkMappingSelection,
    setFilterModel,
    setIsAlertToUnlockOpen,
    setIsModalOpen,
    setIsScreenLocked,
    setIsSidePanelOpen,
    setSelectedFund,
    setSelectionModel,
    tab,
    transBuckets,
    transOptions,
    transTypes
  };
};
